import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { installLimitInterceptor } from "@/lib/limitInterceptor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Token storage
let accessToken = localStorage.getItem("buddy_access_token") || null;
let refreshToken = localStorage.getItem("buddy_refresh_token") || null;

// Publish-subscribe so components (AuthCallback) can push a freshly-issued
// pair of tokens into the module vars + localStorage in a single call — the
// bug this fixes: previously AuthCallback wrote to localStorage but the
// module-level `accessToken` above stayed null, so the axios interceptor sent
// no Authorization header on /auth/me → checkAuth short-circuited → user
// was booted back to /login and had to click "Continue with Google" a 2nd time.
export function setSessionTokens(access, refresh) {
  accessToken = access || null;
  refreshToken = refresh || null;
  if (access) localStorage.setItem("buddy_access_token", access);
  else localStorage.removeItem("buddy_access_token");
  if (refresh) localStorage.setItem("buddy_refresh_token", refresh);
  else localStorage.removeItem("buddy_refresh_token");
}

export function clearSessionTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("buddy_access_token");
  localStorage.removeItem("buddy_refresh_token");
}

const API = axios.create({ baseURL: `${BACKEND_URL}/api` });

// Register the global 402 "limit_reached" handler on this custom axios
// instance too — otherwise pages that call `API.post(...)` (e.g. Cover Letter)
// bypass the modal.
installLimitInterceptor(API);

// Attach token to every request
API.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// On 401, try refresh once, then fail
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (token) prom.resolve(token);
    else prom.reject(error);
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        if (refreshToken) {
          const { data } = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
          if (data.access_token) {
            accessToken = data.access_token;
            localStorage.setItem("buddy_access_token", accessToken);
            processQueue(null, accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return API(originalRequest);
          }
        }
        throw new Error("No refresh token");
      } catch (refreshError) {
        processQueue(refreshError, null);
        accessToken = null;
        refreshToken = null;
        localStorage.removeItem("buddy_access_token");
        localStorage.removeItem("buddy_refresh_token");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

// Notify the Buddy browser extension / mobile WebView that auth state changed.
// - Extension's auth-bridge.js listens for window.postMessage and syncs the
//   token into chrome.storage so the popup re-renders instantly.
// - React Native / iOS / Android WebViews receive the token via their own
//   postMessage bridge (window.ReactNativeWebView / window.webkit.messageHandlers).
function notifyExtension(action = "login") {
  const token = localStorage.getItem("buddy_access_token") || null;
  const refresh = localStorage.getItem("buddy_refresh_token") || null;
  const payload = { source: "buddy-web", type: "BUDDY_AUTH_CHANGED", action, token, refresh };
  // 1) Browser extension (Chrome) — content script listens on window.message
  try { window.postMessage(payload, "*"); } catch (_) {}
  // 2) React Native WebView (Android + iOS)
  try { if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify(payload)); } catch (_) {}
  // 3) iOS native WKWebView with buddyAuth handler
  try { if (window.webkit?.messageHandlers?.buddyAuth) window.webkit.messageHandlers.buddyAuth.postMessage(payload); } catch (_) {}
  // 4) Android native JS-interface (BuddyAndroid.onAuthChanged)
  try { if (window.BuddyAndroid?.onAuthChanged) window.BuddyAndroid.onAuthChanged(JSON.stringify(payload)); } catch (_) {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!accessToken) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      accessToken = null;
      refreshToken = null;
      localStorage.removeItem("buddy_access_token");
      localStorage.removeItem("buddy_refresh_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    if (data.access_token) {
      accessToken = data.access_token;
      refreshToken = data.refresh_token;
      localStorage.setItem("buddy_access_token", accessToken);
      localStorage.setItem("buddy_refresh_token", refreshToken);
    }
    setUser(data);
    notifyExtension("login");
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await API.post("/auth/register", { email, password, name });
    if (data.access_token) {
      accessToken = data.access_token;
      refreshToken = data.refresh_token;
      localStorage.setItem("buddy_access_token", accessToken);
      localStorage.setItem("buddy_refresh_token", refreshToken);
    }
    setUser(data);
    notifyExtension("register");
    return data;
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {}
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem("buddy_access_token");
    localStorage.removeItem("buddy_refresh_token");
    setUser(false);
    notifyExtension("logout");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { API, BACKEND_URL };
